/**
 * Date disambiguation
 * --------------------
 * Tables store dates as yyyy-mm-dd, but parties print them in many layouts and the
 * same string can be ambiguous (05/06/12 = May 6, Jun 5, or … '12). This resolves a
 * raw printed date token to ISO, using, in priority order:
 *   1. self-disambiguation from the digits (a part >12 is the day; a 4-digit part is
 *      the year; a month name fixes the month) — confident, no party needed. A month
 *      name between two short numbers ("22 Dec 06") is NOT explicit: it is 22 Dec 2006
 *      (DMY) or 2022 Dec 06 (YMD), so it goes through steps 2–4 like a numeric date.
 *      A reading that lands after `maxIso` (today) is dropped as impossible;
 *   2. the PARTY's learned format (site_busi_date_format);
 *   3. the COUNTRY default format (site_mstr_countries.default_date_format_id);
 *   4. otherwise → ambiguous (the UI asks the user, then we LEARN the party's format).
 *
 * Pure + unit-tested (dateDisambiguate.test.ts). The `formats` arg is the
 * site_mstr_date_formats catalog; party/country are date_format_id values.
 */

export interface DateFormatRow {
  date_format_id: number;
  format_code: string;
  token_order: string;   // 'YMD' | 'MDY' | 'DMY'
  separator?: string;
  year_digits?: number;
  month_style?: string;  // 'numeric' | 'short' | 'long'
}

export type TokenOrder = 'YMD' | 'MDY' | 'DMY';

export interface InterpretResult {
  iso: string;                 // yyyy-mm-dd, or '' if unresolved
  order: TokenOrder | null;    // the resolved token order (null when ambiguous)
  formatId: number | null;     // best-matching catalog id for the resolved order
  ambiguous: boolean;          // true => needs user confirmation
  source: 'self' | 'party' | 'country' | 'none';
  candidates: { order: TokenOrder; iso: string }[];  // when ambiguous, the plausible reads
}

// ---------------------------------------------------------------------------
// RULE (owner, 2026-10-17): a document that carries a DATE RANGE ("Date Range:
// 11/1/2025 To 11/30/2025", "Nov 1 - Nov 30, 2025", "Period 2025-11-01 through
// 2025-11-30") is dated by the END of the range. Applies to every reader — the
// local parser, the stored raw date, the AI prompts.
// ---------------------------------------------------------------------------

const MON_RE = '(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\\.?';
// A full date: 11/30/2025 · 2025-11-30 · Nov 30, 2025 · 2025 Nov 30 · 30 Nov 2025
const FULL_DATE_SRC =
  `\\d{1,4}[/\\-.]\\d{1,2}[/\\-.]\\d{1,4}` +
  `|${MON_RE}\\s+\\d{1,2},?\\s+\\d{2,4}` +
  `|\\d{4}\\s+${MON_RE},?\\s+\\d{1,2}` +
  `|\\d{1,2}\\s+${MON_RE}\\s+\\d{2,4}`;
// The start of a range may leave out the year the end gives: "Nov 1 - Nov 30, 2025", "11/1 - 11/30/2025".
const RANGE_START_SRC = `${FULL_DATE_SRC}|${MON_RE}\\s+\\d{1,2}|\\d{1,2}\\s+${MON_RE}|\\d{1,2}[/.]\\d{1,2}`;
const RANGE_RE = new RegExp(
  `\\b(?:${RANGE_START_SRC})\\s*(?:to|through|thru|till|until|[-–—~])\\s*(${FULL_DATE_SRC})(?!\\d)`, 'i');

/** The END date of the first date range in `text` (exactly as printed), or '' when there is none. */
export function findRangeEndDate(text: string): string {
  const m = String(text || '').match(RANGE_RE);
  return m ? m[1].trim() : '';
}

/** A printed date that is a range reads as its end date; anything else is returned unchanged. */
export function rangeEndDate(raw: string): string {
  return findRangeEndDate(raw) || String(raw || '');
}

const MONTHS: Record<string, number> = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8,
  sep: 9, sept: 9, september: 9, oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12,
};

const pad = (n: number) => String(n).padStart(2, '0');
const normYear = (y: number, len: number) => (len >= 3 ? y : 2000 + y);

function toIso(y: number, m: number, d: number): string | null {
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
}

function orderOf(formats: DateFormatRow[], id?: number | null): TokenOrder | null {
  if (!id) return null;
  const f = formats.find(x => x.date_format_id === id);
  const o = f?.token_order as TokenOrder | undefined;
  return o === 'YMD' || o === 'MDY' || o === 'DMY' ? o : null;
}

/**
 * Pick the catalog id that best matches a resolved (order, separator, year length).
 * `monthStyle` ('short' | 'long') narrows to the month-NAME layouts when the printed
 * date spelled the month out; numeric dates leave it undefined.
 */
export function findFormatId(formats: DateFormatRow[], order: TokenOrder, separator?: string, yearDigits?: number, monthStyle?: string): number | null {
  let sameOrder = formats.filter(f => f.token_order === order);
  if (!sameOrder.length) return null;
  if (monthStyle) {
    const named = sameOrder.filter(f => f.month_style === monthStyle);
    const anyNamed = named.length ? named : sameOrder.filter(f => f.month_style === 'short' || f.month_style === 'long');
    if (anyNamed.length) {
      const byYear = anyNamed.find(f => yearDigits == null || f.year_digits === yearDigits);
      return (byYear || anyNamed[0]).date_format_id;
    }
  }
  const exact = sameOrder.find(f =>
    (separator == null || f.separator === separator) &&
    (yearDigits == null || f.year_digits === yearDigits));
  if (exact) return exact.date_format_id;
  const sep = sameOrder.find(f => separator != null && f.separator === separator);
  return (sep || sameOrder[0]).date_format_id;
}

type Reading = { order: TokenOrder; iso: string; formatId: number | null };

/**
 * Every calendar-valid way to read a printed date, ignoring priors. Used both to
 * interpret month-name dates and to work out which layout a user's correction implies.
 */
export function allReadings(raw: string, formats: DateFormatRow[]): Reading[] {
  const t = rangeEndDate(raw).trim().toLowerCase();
  const out: Reading[] = [];
  const add = (order: TokenOrder, iso: string | null, sep?: string, yd?: number, ms?: string) => {
    if (iso && !out.some(r => r.iso === iso && r.order === order)) out.push({ order, iso, formatId: findFormatId(formats, order, sep, yd, ms) });
  };
  const style = (name: string) => (name.length <= 4 ? 'short' : 'long');

  const iso0 = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso0) { add('YMD', toIso(+iso0[1], +iso0[2], +iso0[3]), '-', 4); return out; }

  // "Month DD, YYYY" / "Mon DD YY"  (MDY)
  let m = t.match(/^([a-z]+)\.?\s+(\d{1,2}),?\s+(\d{2,4})$/);
  if (m && MONTHS[m[1]]) {
    add('MDY', toIso(normYear(+m[3], m[3].length), MONTHS[m[1]], +m[2]), ' ', m[3].length >= 3 ? 4 : 2, style(m[1]));
    return out;
  }
  // "YYYY Mon DD"  (YMD, 4-digit year first)
  m = t.match(/^(\d{4})\s+([a-z]+)\.?,?\s+(\d{1,2})$/);
  if (m && MONTHS[m[2]]) { add('YMD', toIso(+m[1], MONTHS[m[2]], +m[3]), ' ', 4, style(m[2])); return out; }
  // "DD Mon YYYY" (DMY) — or, with a 2-digit tail, "DD Mon YY" (DMY) vs "YY Mon DD" (YMD)
  m = t.match(/^(\d{1,2})\s+([a-z]+)\.?,?\s+(\d{2,4})$/);
  if (m && MONTHS[m[2]]) {
    const mon = MONTHS[m[2]], A = +m[1], C = +m[3], ms = style(m[2]);
    if (m[3].length >= 3) { add('DMY', toIso(C, mon, A), ' ', 4, ms); return out; }
    add('DMY', toIso(2000 + C, mon, A), ' ', 2, ms);
    add('YMD', toIso(2000 + A, mon, C), ' ', 2, ms);
    return out;
  }

  // Numeric: three parts separated by / - .
  m = t.match(/^(\d{1,4})([/\-.])(\d{1,2})[/\-.](\d{1,4})$/);
  if (!m) return out;
  const sep = m[2], A = +m[1], B = +m[3], C = +m[4], la = m[1].length, lc = m[4].length;
  if (m[3].length > 2) return out;
  if (la <= 2 || la === 4) add('YMD', lc <= 2 ? toIso(normYear(A, la), B, C) : null, sep, la === 4 ? 4 : 2);
  if (la <= 2 && (lc <= 2 || lc === 4)) {
    add('DMY', toIso(normYear(C, lc), B, A), sep, lc === 4 ? 4 : 2);
    add('MDY', toIso(normYear(C, lc), A, B), sep, lc === 4 ? 4 : 2);
  }
  return out;
}

/**
 * The layout a confirmed date implies: which reading of `raw` produces `iso`?
 * This is what gets LEARNED for the party — from the date the user actually filed,
 * not from the first guess (a corrected guess must not teach the wrong layout).
 * Returns null when the filed date is not a reading of the printed one, or when two
 * layouts give the same day (e.g. 05/05/24) and so prove nothing.
 */
export function formatFromConfirmedDate(raw: string, iso: string, formats: DateFormatRow[]): Reading | null {
  if (!raw || !iso) return null;
  const hits = allReadings(raw, formats).filter(r => r.iso === iso);
  return hits.length === 1 ? hits[0] : null;
}

/**
 * Could `raw` have been printed in this layout? Same separator, same year length, a
 * month name where the layout has one, and a calendar-valid reading in its order.
 * "12/06/05" fits YY/MM/DD, DD/MM/YY and MM/DD/YY but not MM/DD/YYYY — which is how a
 * vendor's several layouts are told apart without any words to go by.
 */
export function formatFitsRawDate(raw: string, format: DateFormatRow, formats: DateFormatRow[]): boolean {
  const t = rangeEndDate(raw).trim().toLowerCase();
  if (!t || !format) return false;
  if (!allReadings(t, formats).some(r => r.order === format.token_order)) return false;
  const named = /[a-z]/.test(t);
  const formatNamed = format.month_style === 'short' || format.month_style === 'long';
  if (named !== formatNamed) return false;
  const yearDigits = /(^|\D)\d{4}(\D|$)/.test(t) ? 4 : 2;
  if (format.year_digits != null && Number(format.year_digits) !== yearDigits) return false;
  if (named) return true;
  const sep = t.match(/^\d+([/\-.])/);
  return !format.separator || !sep || format.separator === sep[1];
}

/** Month-name forms. The month is explicit; day vs. year may not be ("22 Dec 06"). */
function parseMonthName(
  raw: string, formats: DateFormatRow[], partyOrder: TokenOrder | null, countryOrder: TokenOrder | null, maxIso?: string,
): InterpretResult | null {
  if (!/[a-z]/i.test(raw)) return null;
  let reads = allReadings(raw, formats);
  if (!reads.length) return null;
  const done = (r: Reading, source: 'self' | 'party' | 'country'): InterpretResult =>
    ({ iso: r.iso, order: r.order, formatId: r.formatId, ambiguous: false, source, candidates: [{ order: r.order, iso: r.iso }] });
  if (reads.length === 1) return done(reads[0], 'self');
  // A document cannot be dated after today — drop readings that are.
  if (maxIso) {
    const past = reads.filter(r => r.iso <= maxIso);
    if (past.length === 1) return done(past[0], 'self');
    if (past.length) reads = past;
  }
  const byParty = partyOrder && reads.find(r => r.order === partyOrder);
  if (byParty) return done(byParty, 'party');
  const byCountry = countryOrder && reads.find(r => r.order === countryOrder);
  if (byCountry) return done(byCountry, 'country');
  return { iso: '', order: null, formatId: null, ambiguous: true, source: 'none', candidates: reads.map(r => ({ order: r.order, iso: r.iso })) };
}

/**
 * Interpret a raw printed date token to ISO.
 * opts: { formats, partyFormatId?, countryFormatId?, maxIso? }  (maxIso = today, yyyy-mm-dd)
 */
export function interpretDate(
  raw: string,
  opts: { formats: DateFormatRow[]; partyFormatId?: number | null; countryFormatId?: number | null; maxIso?: string },
): InterpretResult {
  raw = rangeEndDate(raw);
  const res = interpretDateRaw(raw, opts);
  const maxIso = opts.maxIso;
  if (!maxIso) return res;
  // A document cannot be dated after today. "26-09-29" read day-first is 2029-09-26;
  // the only reading that has already happened is 2026-09-29 (YY-MM-DD).
  const formats = opts.formats || [];
  const past = allReadings(raw, formats).filter(r => r.iso <= maxIso);
  const pick = (r: Reading, source: InterpretResult['source']): InterpretResult =>
    ({ iso: r.iso, order: r.order, formatId: r.formatId, ambiguous: false, source, candidates: [{ order: r.order, iso: r.iso }] });
  if (res.iso && res.iso > maxIso) {
    if (!past.length) return res;                       // nothing plausible — leave the read for the user to fix
    const days = Array.from(new Set(past.map(r => r.iso)));
    if (days.length === 1) return pick(past[0], 'self');
    const partyOrder = orderOf(formats, opts.partyFormatId), countryOrder = orderOf(formats, opts.countryFormatId);
    const byParty = partyOrder && past.find(r => r.order === partyOrder);
    if (byParty) return pick(byParty, 'party');
    const byCountry = countryOrder && past.find(r => r.order === countryOrder);
    if (byCountry) return pick(byCountry, 'country');
    return { iso: '', order: null, formatId: null, ambiguous: true, source: 'none', candidates: past.map(r => ({ order: r.order, iso: r.iso })) };
  }
  if (res.ambiguous) {
    const cands = res.candidates.filter(c => c.iso <= maxIso);
    if (cands.length === 1) { const r = past.find(x => x.iso === cands[0].iso && x.order === cands[0].order); if (r) return pick(r, 'self'); }
    if (cands.length) return { ...res, candidates: cands };
  }
  return res;
}

function interpretDateRaw(
  raw: string,
  opts: { formats: DateFormatRow[]; partyFormatId?: number | null; countryFormatId?: number | null; maxIso?: string },
): InterpretResult {
  const blank: InterpretResult = { iso: '', order: null, formatId: null, ambiguous: false, source: 'none', candidates: [] };
  const text = (raw || '').trim();
  if (!text) return blank;
  const formats = opts.formats || [];
  const partyOrder = orderOf(formats, opts.partyFormatId);
  const countryOrder = orderOf(formats, opts.countryFormatId);

  // Already ISO?
  const iso0 = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso0) {
    const got = toIso(+iso0[1], +iso0[2], +iso0[3]);
    if (got) return { iso: got, order: 'YMD', formatId: findFormatId(formats, 'YMD', '-', 4), ambiguous: false, source: 'self', candidates: [{ order: 'YMD', iso: got }] };
  }

  const mn = parseMonthName(text, formats, partyOrder, countryOrder, opts.maxIso);
  if (mn) return mn;

  // Numeric: three parts separated by / - .
  const m = text.match(/^(\d{1,4})([/\-.])(\d{1,2})[/\-.](\d{1,4})$/);
  if (!m) return blank;
  const sep = m[2];
  const A = +m[1], B = +m[3], C = +m[4];
  const la = m[1].length, lc = m[4].length;
  const fourIdx = [la, m[3].length, lc].findIndex(l => l === 4);

  const prefDM: TokenOrder | null =
    partyOrder === 'DMY' || partyOrder === 'MDY' ? partyOrder
    : countryOrder === 'DMY' || countryOrder === 'MDY' ? countryOrder : null;
  const prefSource: 'party' | 'country' = partyOrder === 'DMY' || partyOrder === 'MDY' ? 'party' : 'country';

  // Helper: resolve a "year-last" pair (A,B) with year yLen-digit at C.
  const resolveYearLast = (): InterpretResult => {
    const y = normYear(C, lc);
    if (A > 12 && B <= 12) { const iso = toIso(y, B, A); return iso ? mk(iso, 'DMY', 'self') : blank; }
    if (B > 12 && A <= 12) { const iso = toIso(y, A, B); return iso ? mk(iso, 'MDY', 'self') : blank; }
    if (A <= 12 && B <= 12) {
      if (prefDM) { const iso = prefDM === 'DMY' ? toIso(y, B, A) : toIso(y, A, B); return iso ? mk(iso, prefDM, prefSource) : blank; }
      const dmy = toIso(y, B, A), mdy = toIso(y, A, B);
      const cands = [dmy && { order: 'DMY' as TokenOrder, iso: dmy }, mdy && { order: 'MDY' as TokenOrder, iso: mdy }].filter(Boolean) as { order: TokenOrder; iso: string }[];
      return { iso: '', order: null, formatId: null, ambiguous: true, source: 'none', candidates: cands };
    }
    return blank; // both > 12 → invalid
  };
  const mk = (iso: string, order: TokenOrder, source: 'self' | 'party' | 'country'): InterpretResult =>
    ({ iso, order, formatId: findFormatId(formats, order, sep, lc >= 3 || la >= 3 ? 4 : 2), ambiguous: false, source, candidates: [{ order, iso }] });

  if (fourIdx === 0) { // YMD (4-digit year first)
    const y = normYear(A, la);
    const [mm, dd] = B > 12 && C <= 12 ? [C, B] : [B, C];
    const iso = toIso(y, mm, dd);
    return iso ? mk(iso, 'YMD', 'self') : blank;
  }
  if (fourIdx === 2) return resolveYearLast();          // year last (4-digit)
  if (fourIdx === 1) return blank;                       // 4-digit in the middle → unsupported

  // No 4-digit part: YMD only if a prior says so, else assume year-last.
  if (partyOrder === 'YMD' || countryOrder === 'YMD') {
    const y = normYear(A, la);
    const [mm, dd] = B > 12 && C <= 12 ? [C, B] : [B, C];
    const iso = toIso(y, mm, dd);
    return iso ? mk(iso, 'YMD', partyOrder === 'YMD' ? 'party' : 'country') : blank;
  }
  return resolveYearLast();
}


// ---------------------------------------------------------------------------
// A date read as later than today is almost always a misread (OCR turning 2023 into
// 2823, or the wrong token being picked). Before the user is asked, look again.
// ---------------------------------------------------------------------------

const DATE_TOKEN_RE = new RegExp(
  '\\b\\d{1,4}[/\\-.]\\d{1,2}[/\\-.]\\d{1,4}\\b' +
  '|\\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\\.?\\s+\\d{1,2},?\\s+\\d{2,4}\\b' +
  '|\\b\\d{4}\\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\\.?,?\\s+\\d{1,2}\\b' +
  '|\\b\\d{1,2}\\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\\.?\\s+\\d{2,4}\\b', 'gi');

/** Every date-looking token in a document's text, in reading order. */
export function findDateTokens(text: string): string[] {
  return (String(text || '').match(DATE_TOKEN_RE) || []).map(t => t.trim());
}

/**
 * A future year with one misread digit: keep the last two digits and assume this
 * century (2823 → 2023, 2203 → 2003). Returned only when the result is not itself in
 * the future — and only ever as a SUGGESTION for the user to confirm.
 */
export function repairFutureYear(iso: string, maxIso: string): string | null {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m || !maxIso || iso <= maxIso) return null;
  const fixed = `${2000 + (Number(m[1]) % 100)}-${m[2]}-${m[3]}`;
  return fixed !== iso && fixed <= maxIso ? fixed : null;
}

export interface FutureDateRecheck {
  /** The one plausible date found on a second look — safe to use without asking. */
  iso: string;
  formatId: number | null;
  /** When there is no single answer: plausible dates to offer the user (may be empty). */
  suggestions: { iso: string; order: TokenOrder; formatId: number | null; why: 'in-document' | 'year-repair' }[];
}

/**
 * Second look for a date that was read as later than today. Re-reads every date token
 * in the document text, keeping only readings that have already happened (and are not
 * absurdly old), plus a year-repair of the future read itself.
 *   - exactly one such date in the document and no competing repair → `iso` (use it);
 *   - otherwise → `iso` is '' and `suggestions` lists what to offer; an empty list
 *     means nothing plausible was found and the user must type or confirm the date.
 */
export function recheckFutureDate(
  futureIso: string,
  text: string,
  opts: { formats: DateFormatRow[]; partyFormatId?: number | null; countryFormatId?: number | null; maxIso: string; minIso?: string },
): FutureDateRecheck {
  const minIso = opts.minIso || '1990-01-01';
  const inDoc: FutureDateRecheck['suggestions'] = [];
  for (const tok of findDateTokens(text)) {
    const di = interpretDate(tok, opts);
    const reads = di.iso && !di.ambiguous
      ? [{ iso: di.iso, order: di.order as TokenOrder, formatId: di.formatId }]
      : allReadings(tok, opts.formats || []);
    for (const r of reads) {
      if (r.iso > opts.maxIso || r.iso < minIso) continue;
      if (!inDoc.some(x => x.iso === r.iso)) inDoc.push({ iso: r.iso, order: r.order, formatId: r.formatId, why: 'in-document' });
    }
  }
  const repaired = repairFutureYear(futureIso, opts.maxIso);
  const suggestions = [...inDoc];
  if (repaired && !suggestions.some(x => x.iso === repaired)) {
    suggestions.unshift({ iso: repaired, order: 'YMD', formatId: null, why: 'year-repair' });
  }
  // One date in the document, and the repair (if any) agrees with it → no need to ask.
  if (inDoc.length === 1 && (!repaired || repaired === inDoc[0].iso)) {
    return { iso: inDoc[0].iso, formatId: inDoc[0].formatId, suggestions: [] };
  }
  return { iso: '', formatId: null, suggestions };
}
